import React, { FC } from "react";
import { Box, FormControl, Text, TextInput } from "@primer/react";
import { Banner } from "@primer/react/experimental";
import { StatusBreakdown } from "./RegistrationStatusContent";

interface DeleteContentProps {
  selectionCount: number;
  preflightCounts?: Record<string, number>;
  confirmValue: string;
  onConfirmValueChange: (value: string) => void;
  requiredConfirmText: string;
}

// RegistrationDeleteExecutor only deletes cancelled registrations — everything
// else is skipped. Keep this copy in sync with its actionable? check.
const describeDeleteRow = (status: string) =>
  status === "cancelled"
    ? "These rows will be permanently deleted"
    : "Only cancelled registrations can be deleted — these rows will be skipped";

export const DeleteContent: FC<DeleteContentProps> = ({
  selectionCount,
  preflightCounts,
  confirmValue,
  onConfirmValueChange,
  requiredConfirmText,
}) => {
  const rowLabel = selectionCount === 1 ? "row" : "rows";
  const registrationLabel = selectionCount === 1 ? "registration" : "registrations";

  const deletableCount = preflightCounts ? (preflightCounts.cancelled ?? 0) : undefined;
  const skippedStatuses = preflightCounts
    ? Object.keys(preflightCounts).filter((status) => status !== "cancelled")
    : undefined;

  if (deletableCount === 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Banner
          variant="warning"
          title={`None of the selected ${registrationLabel} can be deleted. Only cancelled registrations can be deleted — cancel ${selectionCount === 1 ? "it" : "them"} first, then delete.`}
        />
        {preflightCounts && (
          <StatusBreakdown
            counts={preflightCounts}
            targetStatus="cancelled"
            describeRow={describeDeleteRow}
            skippedStatuses={skippedStatuses}
          />
        )}
      </Box>
    );
  }

  const deleteLabel = (count: number) =>
    `${count} ${count === 1 ? "attendee" : "attendees"}`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Text as="div" fontSize={1}>
        {/* When only part of the selection is deletable, say so up front —
            "Deleting 5 rows" over a "2 attendees" banner was the last
            remaining count that described the selection instead of the
            action (GR-2164 review). */}
        Deleting{" "}
        <Text as="span" fontWeight="bold">
          {deletableCount !== undefined && deletableCount !== selectionCount
            ? `${deletableCount} of ${selectionCount} selected ${rowLabel}`
            : `${selectionCount} ${rowLabel}`}
        </Text>
      </Text>

      <Banner
        variant="critical"
        title={`You are about to permanently delete the registration for ${deleteLabel(deletableCount ?? selectionCount)}. This action cannot be undone. Any outstanding refunds must be handled manually.`}
      />

      {preflightCounts && (
        <StatusBreakdown
          counts={preflightCounts}
          targetStatus="cancelled"
          describeRow={describeDeleteRow}
          skippedStatuses={skippedStatuses}
        />
      )}

      <FormControl>
        <FormControl.Label>To confirm, type &lsquo;{requiredConfirmText}&rsquo; below</FormControl.Label>
        <TextInput
          block
          value={confirmValue}
          onChange={(e) => onConfirmValueChange(e.target.value)}
          aria-label={`Type ${requiredConfirmText} to confirm`}
        />
      </FormControl>

      <Text as="div" fontSize={1}>
        Are you sure you want to permanently delete{" "}
        <Text as="span" fontWeight="bold">{deleteLabel(deletableCount ?? selectionCount)}</Text>?
        {" "}Only cancelled registrations will be deleted — other statuses are skipped.
      </Text>
    </Box>
  );
};
